import React, { useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Update, GetById, GetAllUOm, GetAllCategory } from "adminApi/ItemsListApi/ItemsList";
import fields from "../json/ProductListFields.json";
import { validateForm } from "utils/validation";
import { ProductsEditRules } from "validation/ValidationRules";
import Spinner from "Common/Globle/Spinner";
import EditProductForm from "./Componates/EditProductForm";
import useFormChange from "Hooks/useFormChange";
import useFormSubmit from "Hooks/useFormSubmit";
import useFetchFormDataById from "Hooks/useFetchFormDataById";
import useFetchAllData from "Hooks/useFetchAllData";

const EditProduct = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { editdata } = fields;

  const initialValues = editdata.reduce((acc, field) => ({ ...acc, [field.name]: "" }), {});

  const { values, errors, handleChange, setErrors, setValues } = useFormChange(
    initialValues,
    ProductsEditRules
  );

  // Capture original values once after the record loads — used for live delta calculation
  const originalTotalRef = useRef(0);
  const originalAvailableRef = useRef(0);
  const originalsSet = useRef(false); // guard: only set once

  // Pre-fill form with existing record — pass setValues directly (stable reference)
  const { loading: loadingFetch } = useFetchFormDataById(id, editdata, GetById, setValues);

  // After data loads, capture the originals exactly once
  useEffect(() => {
    if (originalsSet.current) return;
    const total = parseInt(values.stock_quantity) || 0;
    const available = parseInt(values.available_quantity) || 0;
    // Only set when data has actually arrived (both > 0 or total > 0)
    if (total > 0 || available > 0) {
      originalTotalRef.current = total;
      originalAvailableRef.current = available;
      originalsSet.current = true;
    }
  }, [values.stock_quantity, values.available_quantity]);

  /**
   * Custom onChange for stock_quantity field only.
   * Computes live available_quantity = original_available + (new_total - original_total).
   * Blocks input if it would push available below 0.
   * All other fields use the standard handleChange.
   */
  const handleFieldChange = (e) => {
    const { name, value } = e.target;

    if (name !== "stock_quantity") {
      handleChange(e);
      return;
    }

    const newTotal = parseInt(value) || 0;
    const delta = newTotal - originalTotalRef.current;
    const newAvailable = originalAvailableRef.current + delta;

    // Block: cannot decrease total if it would make available < 0
    if (newAvailable < 0) return;

    // Update both fields together — no extra render cycle
    setValues((prev) => ({
      ...prev,
      stock_quantity: newTotal,
      available_quantity: Math.max(0, newAvailable),
    }));
  };

  const { loading: loadingSubmit, handleSubmit } = useFormSubmit(
    async (formValues) => {
      const formData = new FormData();
      // Strip available_quantity (display only) — stock_quantity is submitted
      const { available_quantity: _a, ...editable } = formValues;
      Object.entries(editable).forEach(([key, val]) => formData.append(key, val ?? ""));
      return await Update({ id, data: formData });
    },
    (vals) => {
      const err = validateForm(vals, ProductsEditRules);
      setErrors(err);
      return err;
    },
    () => navigate(-1),
    "Product updated successfully."
  );

  const { data: CategoryOPction = [], loading: loadingCategory } = useFetchAllData(GetAllCategory);
  const { data: UOMOpction = [], loading: loadingUOM } = useFetchAllData(GetAllUOm);

  return (
    <>
      {(loadingFetch || loadingSubmit || loadingCategory || loadingUOM) && <Spinner isSpinner />}
      <EditProductForm
        fields={editdata}
        values={values}
        errors={errors}
        handleChange={handleFieldChange}
        handleSubmit={(e) => handleSubmit(e, values)}
        loading={loadingSubmit}
        CategoryOPction={CategoryOPction}
        UOMOpction={UOMOpction}
      />
    </>
  );
};

export default EditProduct;
